/* eslint-disable react-hooks/exhaustive-deps */
import {
	FormControl,
	FormHelperText,
	InputLabel,
	TextField,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Paper from "@material-ui/core/Paper";

import getList from "../../../lib/api/getList";

const CustomPaper = (props) => {
	return <Paper square={true} {...props} />;
};
/**
 * @COMPONENT_NAME FormItemCommonAutocomplete
 * @DESCRIPTION 자동완성 기능이 포함된 입력 컴포넌트로, 공통코드 정보를 기반으로 데이터를 불러와 자동완성 리스트를 구성합니다.
 * @PROPS
 *    - {string} id - 컴포넌트 고유 ID (label 연결 등)
 *    - {string} className - CSS 클래스 이름
 *    - {string} label - 입력 필드 라벨 텍스트
 *    - {string} helperText - 입력 하단에 표시될 설명 텍스트
 *    - {object} InputLabelProps - MUI InputLabel에 전달할 props
 *    - {Function} onChange - 항목 선택 시 호출되는 콜백 (선택된 항목의 value 값 전달)
 *    - {...object} others - 기타 추가 전달되는 props (FormControl, Autocomplete 등에 전달됨)
 *    - {object} column - 자동완성 동작을 제어하는 구성 정보 (commonCodeInfo 등 포함)
 * 		ex)
		{
			formOrder = 190;
			formGroup = 1;
			formItemType = "commoncode";
			filter = {		
				type: "valueEqual",
				formOrder: 25,
				formGroup: 0,
			};
			commonCodeInfo = {
					mstCodeId: "A001",
					useEmptyValue: true,
					emptyName: "term.none",
					sortByName: true,
					overrideLabel: "term.commonCode",
		};
 * @PASSTHROUGH_PROPS
 *    - {boolean} autoFocus - TextField에 자동 포커스를 줄지 여부
 *    - {object} inputProps - TextField의 inputProps (예: tabIndex 등)
 *
 * @CALLBACKS
 *    - {Function} onChange - 자동완성 항목 선택 시 호출되며, 선택된 value를 인자로 전달.
 */
function FormItemCommonAutoComplete(props) {
	const { t } = useTranslation();

	const {
		id,
		className,
		column,
		label,
		helperText,
		InputLabelProps,
		onChange,
		...others
	} = props;
	const [slaveSelectItems, setSlaveSelectItems] = useState([]);
	const commonCodeInfo = column.commonCodeInfo;

	useEffect(() => {
		let mounted = true;

		(async () => {
			try {
				let response = await getList("VwCommonCodeSlave/" + commonCodeInfo.mstCodeId);
				if (mounted) {
					let selectItems = response?.data?.map((result) => {
						if (Array.isArray(commonCodeInfo.nameColumn)) {
							let name = "";
							let first = true;

							for (const columnName of commonCodeInfo.nameColumn) {
								if (!first && result[columnName]) {
									name += commonCodeInfo.seperator || ", ";
								}

								first = false;
								name += result[columnName] || "";
							}

							return {
								name: name,
								value: result[commonCodeInfo.valueColumn],
							};
						} else {
							return {
								name: result[commonCodeInfo.nameColumn],
								value: result[commonCodeInfo.valueColumn],
							};
						}
					});

					if (commonCodeInfo.sortByName) {
						selectItems.sort((itemA, itemB) =>
							("" + itemA.name).localeCompare(itemB.name)
						);
					}

					if (commonCodeInfo.useEmptyValue) {
						setSlaveSelectItems([
							{
								name: t(commonCodeInfo.emptyName),
								value: commonCodeInfo.specifiedValue?.enabled
									? commonCodeInfo.specifiedValue.value
									: "",
							},
							...selectItems,
						]);
					} else {
						setSlaveSelectItems(selectItems);
					}
				}
			} catch (reason) {
				console.error(
					`Get Code List from Table "VwCommonCodeSlave" Failed`
				);
			}
		})();

		return () => {
			mounted = false;
		};
	}, []);

	const selectProps = {
		onChange: (_event, option) => {
			onChange(option ? option.value : "");
		},
		...others,
	};

	if (column.displayEmpty) {
		selectProps.displayEmpty = true;
	}

	if (column.style) {
		selectProps.style = column.style;
	}

	if (column.getOptionDisabled) {
		selectProps.getOptionDisabled = column.getOptionDisabled;
	}

	return (
		<>
			<FormControl
				className={className}
				required={column.required}
				style={column.formStyle}
				{...others}
			>
				<InputLabel
					id={id + "-label"}
					shrink={true}
					{...InputLabelProps}
				>
					{
						commonCodeInfo.overrideLabel ? t(`${commonCodeInfo.overrideLabel}`) : t("term.commonCode")
					}
				</InputLabel>
				<Autocomplete
					id={id}
					style={{ paddingTop: "16px" }}
					PaperComponent={CustomPaper}
					{...selectProps}
					options={slaveSelectItems}
					getOptionLabel={(option) => option.name}
					autoSelect={true}
					renderInput={(params) => {
						const inputProps = params.inputProps;
						inputProps.autoComplete = "chrome-off";
						return (
							<TextField
								{...params}
								autoFocus={props.autoFocus}
								inputProps={inputProps}
							/>
						);
					}}
					tabIndex={props.inputProps?.tabIndex}
					value={
						slaveSelectItems.length > 0
							? slaveSelectItems.find(
									(item) => item.value === selectProps.value
							  )
							: { name: "", value: "" }
					}
				/>

				<FormHelperText>{helperText}</FormHelperText>
			</FormControl>
		</>
	);
}

export default FormItemCommonAutoComplete;
